import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Autocomplete, Box, Button, Chip, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotify } from 'react-admin';
import {
  AgentTrackingNumberUsageCategory,
  ListTrackingNumbersParams,
  TrackingNumberDetail,
  TrackingNumberListItem,
  TrackingNumberStatus,
} from '../../../interfaces/calls';
import voiceService from '../../../services/appointments/calls';
import { CustomTable } from '../../common/CustomTable';
import { TrackingNumberDetailModal } from './TrackingNumberDetailModal';
import { TrackingNumberFormModal } from './TrackingNumberFormModal';
import './tracking-numbers-center.css';

type Props = {
  breadcrumbs: ReactNode;
};

const STATUS_COLORS = {
  [TrackingNumberStatus.ACTIVE]: '#4caf50',
  [TrackingNumberStatus.INACTIVE]: '#9e9e9e',
};

const STATUS_DISPLAY = {
  [TrackingNumberStatus.ACTIVE]: 'Active',
  [TrackingNumberStatus.INACTIVE]: 'Inactive',
};

const USAGE_CATEGORY_DISPLAY = {
  [AgentTrackingNumberUsageCategory.INCOMING]: 'Incoming',
  [AgentTrackingNumberUsageCategory.OUTGOING]: 'Outgoing',
  [AgentTrackingNumberUsageCategory.SMS]: 'SMS',
};

export const TrackingNumbersCenter = ({ breadcrumbs }: Props) => {
  const [trackingNumbers, setTrackingNumbers] = useState<TrackingNumberListItem[]>([]);
  const [filters, setFilters] = useState<ListTrackingNumbersParams>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedNumber, setSelectedNumber] = useState<TrackingNumberDetail | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState<boolean>(false);
  const [formModalOpen, setFormModalOpen] = useState<boolean>(false);
  const [numberToEdit, setNumberToEdit] = useState<TrackingNumberDetail | null>(null);

  const notify = useNotify();

  const fetchTrackingNumbers = useCallback(async () => {
    try {
      setLoading(true);

      const cleanedFilters = Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== '' && value !== undefined && value !== null) {
          acc[key] = value;
        }
        return acc;
      }, {} as any);

      const response = await voiceService.listTrackingNumbers(cleanedFilters);

      if (!response.data) {
        notify('Failed to fetch tracking numbers', 'error');
        return;
      }

      setTrackingNumbers(response.data);
    } catch (error) {
      notify('Failed to fetch tracking numbers', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, notify]);

  const fetchTrackingNumberDetail = useCallback(
    async (id: number, openDetail = true) => {
      try {
        const response = await voiceService.getTrackingNumberDetail(id);

        if (!response.data) {
          notify('Failed to fetch tracking number details', 'error');
          return null;
        }

        if (openDetail) {
            setSelectedNumber(response.data);
            setDetailModalOpen(true);
        }
        return response.data;
      } catch (error) {
        notify('Failed to fetch tracking number details', 'error');
        return null;
      }
    },
    [notify],
  );

  const handleFilterChanges = (filter?: { [filter: string]: any }) => {
    const updatedFilters = {
      ...filters,
      ...filter,
    };

    setFilters(updatedFilters);
  };

  const handleRefresh = useCallback(() => {
    fetchTrackingNumbers();
  }, [fetchTrackingNumbers]);

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedNumber(null);
    fetchTrackingNumbers(); // Refresh list after modal closes
  };

  const handleFormSuccess = () => {
    fetchTrackingNumbers();
    // If we were editing from detail view, refresh detail view
    if (detailModalOpen && selectedNumber) {
        fetchTrackingNumberDetail(selectedNumber.id);
    }
  };

  const handleCreate = () => {
    setNumberToEdit(null);
    setFormModalOpen(true);
  };

  const handleEdit = async (id: number) => {
    // We might need full details to edit, so fetch them first if not available
    let detail = selectedNumber && selectedNumber.id === id ? selectedNumber : null;
    if (!detail) {
        detail = await fetchTrackingNumberDetail(id, false);
    }
    
    if (detail) {
        setNumberToEdit(detail);
        setFormModalOpen(true);
    }
  };

  useEffect(() => {
    fetchTrackingNumbers();
  }, [filters, fetchTrackingNumbers]);

  const rows = useMemo(
    () =>
      trackingNumbers.map((number) => ({
        number: (
          <Box flexDirection='row' display='flex' alignItems='center' gap={1}>
            <Typography variant='body2' fontWeight='600'>
              {number.trackingNumber}
            </Typography>
            {number.label && (
              <Chip
                size='small'
                label={number.label}
                variant='outlined'
                sx={{ fontSize: '11px' }}
              />
            )}
          </Box>
        ),

        features: (
          <Box display='flex' gap={0.5} flexWrap='wrap'>
            {number.isMainLine && (
              <Chip
                size='small'
                label='Main Line'
                sx={{ fontSize: '11px', backgroundColor: '#2196f3', color: 'white' }}
              />
            )}
            {number.enabledForVoiceCalls && (
              <Chip size='small' label='Voice' variant='outlined' sx={{ fontSize: '11px' }} />
            )}
            {number.enabledForReminders && (
              <Chip size='small' label='Reminders' variant='outlined' sx={{ fontSize: '11px' }} />
            )}
            {number.isToolFree && (
              <Chip size='small' label='Tool free' variant='outlined' sx={{ fontSize: '11px' }} />
            )}
          </Box>
        ),
        agents: (
          <Typography variant='body2'>
            {number.totalAgentsAssigned > 0 ? `${number.totalAgentsAssigned} assigned` : '-'}
          </Typography>
        ),
        callRoute: <Typography variant='body2'>{number.callRouteName || '-'}</Typography>,
        status: (
          <Chip
            size='small'
            label={STATUS_DISPLAY[number.status]}
            sx={{
              fontSize: '12px',
              backgroundColor: STATUS_COLORS[number.status],
              color: 'white',
            }}
          />
        ),
        actions: (
          <Box display='flex'>
            <Tooltip title='View Details'>
                <IconButton size='small' onClick={() => fetchTrackingNumberDetail(number.id)}>
                <InfoOutlinedIcon fontSize='small' />
                </IconButton>
            </Tooltip>
            <Tooltip title='Edit'>
                <IconButton size='small' onClick={() => handleEdit(number.id)}>
                <EditIcon fontSize='small' />
                </IconButton>
            </Tooltip>
          </Box>
        ),
      })),
    [trackingNumbers, fetchTrackingNumberDetail],
  );

  return (
    <Box className='tab-section tracking-numbers-center-section' marginTop={0}>
      <Box className='tracking-numbers-center-header'>
        <Box display='flex' justifyContent='space-between' alignContent='center' marginBottom='8px'>
          {breadcrumbs}
          <Button
            variant='contained'
            color='primary'
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            Create Number
          </Button>
        </Box>

        <Box display='flex' alignItems='flex-start' gap={1} mt={2} mb={3} flexWrap='wrap'>
          {/* Search Filter */}
          <Box display='flex' flexDirection='column' alignItems='flex-start' marginBottom={2}>
            <Typography sx={{ '&.MuiTypography-root': { fontSize: '13px' } }}>Search</Typography>
            <TextField
              size='small'
              placeholder='Search number or label'
              value={filters.search || ''}
              onChange={(e) => handleFilterChanges({ search: e.target.value || undefined })}
              sx={{ width: '200px' }}
            />
          </Box>

          {/* Status Filter */}
          <Box display='flex' flexDirection='column' alignItems='flex-start' marginBottom={2}>
            <Typography sx={{ '&.MuiTypography-root': { fontSize: '13px' } }}>Status</Typography>
            <Autocomplete
              size='small'
              options={Object.values(TrackingNumberStatus)}
              getOptionLabel={(option) => STATUS_DISPLAY[option]}
              onChange={(_, value) => handleFilterChanges({ status: value || undefined })}
              renderInput={(params) => (
                <TextField {...params} placeholder='All statuses' sx={{ width: '150px' }} />
              )}
              sx={{ width: '150px' }}
            />
          </Box>

          {/* Usage Category Filter */}
          <Box display='flex' flexDirection='column' alignItems='flex-start' marginBottom={2}>
            <Typography sx={{ '&.MuiTypography-root': { fontSize: '13px' } }}>
              Usage Type
            </Typography>
            <Autocomplete
              size='small'
              options={Object.values(AgentTrackingNumberUsageCategory)}
              getOptionLabel={(option) => USAGE_CATEGORY_DISPLAY[option]}
              onChange={(_, value) => handleFilterChanges({ usageCategory: value || undefined })}
              renderInput={(params) => (
                <TextField {...params} placeholder='All types' sx={{ width: '150px' }} />
              )}
              sx={{ width: '150px' }}
            />
          </Box>

          {/* Refresh */}
          <Box
            display='flex'
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
            className='tracking-numbers-center-select'>
            <Typography>&nbsp;</Typography>
            <Box display='flex' flexDirection='column' alignItems='center' gap={2}>
              <RefreshIcon onClick={handleRefresh} sx={{ cursor: 'pointer' }} />
            </Box>
          </Box>
        </Box>
      </Box>

      <CustomTable
        columns={[
          { key: 'number', label: 'Number', width: '200px', minWidth: '200px' },
          { key: 'features', label: 'Features', width: '150px', minWidth: '150px' },
          { key: 'agents', label: 'Agents', width: '120px', minWidth: '120px' },
          { key: 'callRoute', label: 'Call Route', width: '180px', minWidth: '180px' },
          { key: 'status', label: 'Status', width: '120px', minWidth: '120px' },
          { key: 'actions', label: 'Actions', width: '100px', minWidth: '100px' },
        ]}
        defaultText='No tracking numbers found'
        key='tracking-numbers-center-table'
        loading={loading}
        rows={rows}
      />

      {selectedNumber && (
        <TrackingNumberDetailModal
          open={detailModalOpen}
          trackingNumber={selectedNumber}
          onClose={handleCloseDetailModal}
          onEdit={() => handleEdit(selectedNumber.id)}
        />
      )}
      
      <TrackingNumberFormModal
        open={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        onSuccess={handleFormSuccess}
        trackingNumberToEdit={numberToEdit}
      />
    </Box>
  );
};
